/**
 * PlanGoalsTree — read-only annual/quarter/sprint tiers (Manager-owned)
 * and editable month/week tiers (rep-owned). Soft-warns when the rep's sum
 * exceeds the manager ceiling (does not block).
 *
 * Annual + quarter come from the user's existing sprint/boy goals + manager
 * inputs (read-only). Month + week ride on rep_period_plan rows (period_type
 * 'month' / 'week') and write through `/api/me/plan`.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/session";

export interface ManagerCeilings {
  annual: number;
  quarter: number;
  sprint: number;
}

export interface RepPlanRow {
  period_type: "month" | "week";
  period_start: string; // ISO
  period_end: string;
  revenue_target: number;
  meetings_target: number;
  outreach_target: number;
  qualified_handoffs_target?: number;
}

export interface PlanGoalsTreeProps {
  ceilings: ManagerCeilings;
  monthPlan?: RepPlanRow | null;
  weekPlans?: RepPlanRow[]; // current month's weeks
  onSaved?: () => void;
  bdrMode?: boolean; // BDRs use qualified_handoffs_target instead of revenue
}

function fmtMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}k`;
  return `$${Math.round(n)}`;
}

export function PlanGoalsTree({ ceilings, monthPlan, weekPlans = [], onSaved, bdrMode = false }: PlanGoalsTreeProps) {
  const [monthValue, setMonthValue] = useState<number>(monthPlan?.revenue_target ?? 0);
  const [monthHandoffs, setMonthHandoffs] = useState<number>(monthPlan?.qualified_handoffs_target ?? 0);
  const [weeks, setWeeks] = useState<RepPlanRow[]>(weekPlans);
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState<string | null>(null);

  useEffect(() => {
    setMonthValue(monthPlan?.revenue_target ?? 0);
    setMonthHandoffs(monthPlan?.qualified_handoffs_target ?? 0);
  }, [monthPlan?.period_start, monthPlan?.revenue_target, monthPlan?.qualified_handoffs_target]);
  useEffect(() => { setWeeks(weekPlans); }, [JSON.stringify(weekPlans.map((w) => w.period_start))]);

  const weekSum = bdrMode
    ? weeks.reduce((s, w) => s + (w.qualified_handoffs_target ?? 0), 0)
    : weeks.reduce((s, w) => s + (w.revenue_target ?? 0), 0);
  const monthCeilingSoft = bdrMode ? null : Math.round(ceilings.quarter / 3);

  useEffect(() => {
    if (bdrMode) {
      setWarn(weekSum > monthHandoffs && monthHandoffs > 0
        ? `Week handoff targets sum to ${weekSum} - exceeds month target of ${monthHandoffs}.`
        : null);
      return;
    }
    if (monthCeilingSoft && monthValue > monthCeilingSoft) {
      setWarn(`Month target ${fmtMoney(monthValue)} exceeds quarter ceiling pace of ${fmtMoney(monthCeilingSoft)}/mo.`);
      return;
    }
    if (monthValue > 0 && weekSum > monthValue) {
      setWarn(`Week targets sum to ${fmtMoney(weekSum)} - exceeds month target of ${fmtMoney(monthValue)}.`);
      return;
    }
    setWarn(null);
  }, [monthValue, monthHandoffs, weekSum, monthCeilingSoft, bdrMode]);

  async function saveMonth() {
    setSaving(true);
    try {
      await api("/api/me/plan", {
        method: "PUT",
        body: JSON.stringify({
          period_type: "month",
          period_start: monthPlan?.period_start,
          period_end: monthPlan?.period_end,
          revenue_target: bdrMode ? 0 : monthValue,
          qualified_handoffs_target: bdrMode ? monthHandoffs : 0,
        }),
      });
      onSaved?.();
    } finally { setSaving(false); }
  }

  async function saveWeek(idx: number, value: number) {
    const w = weeks[idx];
    if (!w) return;
    const next = [...weeks];
    next[idx] = bdrMode
      ? { ...w, qualified_handoffs_target: value }
      : { ...w, revenue_target: value };
    setWeeks(next);
    try {
      await api("/api/me/plan", {
        method: "PUT",
        body: JSON.stringify({
          period_type: "week",
          period_start: w.period_start,
          period_end: w.period_end,
          revenue_target: bdrMode ? 0 : value,
          qualified_handoffs_target: bdrMode ? value : 0,
        }),
      });
      onSaved?.();
    } catch { /* tolerate */ }
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-medium" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
          Goals tree
        </h3>
        <span className="text-[11px] text-neutral-500">Manager owns annual + quarter. You set month + week.</span>
      </div>

      <div className="space-y-2 text-sm">
        {/* Annual (read-only) */}
        <div className="flex items-center justify-between rounded bg-neutral-50 px-3 py-2">
          <span className="text-neutral-700">Annual ceiling</span>
          <span className="font-medium text-neutral-900">{bdrMode ? `${ceilings.annual} handoffs` : fmtMoney(ceilings.annual)}</span>
        </div>
        {/* Quarter (read-only) */}
        <div className="flex items-center justify-between rounded bg-neutral-50 px-3 py-2">
          <span className="text-neutral-700">Quarter ceiling</span>
          <span className="font-medium text-neutral-900">{bdrMode ? `${ceilings.quarter} handoffs` : fmtMoney(ceilings.quarter)}</span>
        </div>
        {/* Sprint (read-only) */}
        <div className="flex items-center justify-between rounded bg-neutral-50 px-3 py-2">
          <span className="text-neutral-700">Jumpstart target</span>
          <span className="font-medium text-neutral-900">{bdrMode ? `${ceilings.sprint} handoffs` : fmtMoney(ceilings.sprint)}</span>
        </div>

        {/* Month (editable) */}
        <div className="flex items-center justify-between rounded border border-neutral-200 px-3 py-2">
          <span className="text-neutral-700">This month</span>
          <span className="flex items-center gap-2">
            <input
              type="number"
              className="w-28 rounded border border-neutral-300 px-2 py-1 text-right text-sm"
              value={bdrMode ? monthHandoffs : monthValue}
              onChange={(e) => bdrMode ? setMonthHandoffs(Number(e.target.value)) : setMonthValue(Number(e.target.value))}
              data-testid="plan-month-input"
            />
            <button
              className="rounded bg-[#0B0B0F] px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
              onClick={saveMonth}
              disabled={saving || !monthPlan}
              data-testid="plan-month-save"
            >Save</button>
          </span>
        </div>

        {/* Weeks (editable) */}
        {weeks.map((w, i) => (
          <div key={w.period_start} className="flex items-center justify-between rounded border border-neutral-200 px-3 py-2">
            <span className="text-neutral-700">Week of {w.period_start}</span>
            <input
              type="number"
              className="w-28 rounded border border-neutral-300 px-2 py-1 text-right text-sm"
              defaultValue={bdrMode ? (w.qualified_handoffs_target ?? 0) : w.revenue_target}
              onBlur={(e) => saveWeek(i, Number(e.target.value))}
              data-testid={`plan-week-input-${i}`}
            />
          </div>
        ))}

        {warn && (
          <div className="mt-2 rounded border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-yellow-900" data-testid="plan-soft-warning">
            {warn}
          </div>
        )}
      </div>
    </div>
  );
}

export default PlanGoalsTree;
